/** Typed wrappers around the API endpoints, grouped by area. */
import type {
  AuthResponse,
  PublicUser,
  ZumbaClassWithBookingState,
  BookingWithClass,
  PaginatedCustomers,
  CrmNote,
  RosterEntry,
  ZumbaClass,
  RegisterRequest,
  LoginRequest,
  UpdateProfileRequest,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  CreateClassRequest,
  UpdateClassRequest,
  PresignUploadResponse,
  AllowedImageType,
  SiteSettings,
  UpdateSettingsRequest,
  ContactRequest,
  UpdateNotificationPrefsRequest,
  UnsubscribeResponse,
  WaiverStatus,
  SignWaiverRequest,
  CustomerOverview,
  BillingSummary,
  LedgerEntry,
  MyBillingResponse,
  CheckoutItem,
  CheckoutResponse,
  BillingPortalResponse,
} from '@grasi/shared';
import { api, uploadToS3 } from './client.js';

/** Billing actions return the refreshed billing slice so the UI can patch in place. */
type BillingResult = { billing: BillingSummary; ledger: LedgerEntry[] };

export const contactApi = {
  send: (body: ContactRequest) => api.post<{ ok: true }>('/contact', body),
};

export const waiverApi = {
  status: () => api.get<{ status: WaiverStatus }>('/me/waiver'),
  sign: (body: SignWaiverRequest) => api.post<{ status: WaiverStatus }>('/me/waiver', body),
  /** Direct links (cookie auth) — usable as anchor hrefs to view/download the PDFs. */
  blankPdfUrl: '/api/waiver/pdf',
  myPdfUrl: '/api/me/waiver/pdf',
};

export const unsubscribeApi = {
  confirm: (token: string) => api.post<UnsubscribeResponse>('/unsubscribe', { token }),
};

export const billingApi = {
  /** Start a Stripe Checkout session; returns the URL to redirect the browser to. */
  checkout: (item: CheckoutItem) => api.post<CheckoutResponse>('/billing/checkout', { item }),
  /** Open the Stripe Billing Customer Portal (manage/cancel subscription); returns the URL. */
  portal: () => api.post<BillingPortalResponse>('/billing/portal'),
};

export const authApi = {
  me: () => api.get<AuthResponse | null>('/auth/me', { throwOnUnauthorized: false }),
  register: (body: RegisterRequest) => api.post<AuthResponse>('/auth/register', body),
  login: (body: LoginRequest) => api.post<AuthResponse>('/auth/login', body),
  logout: () => api.post<{ ok: true }>('/auth/logout'),
  forgotPassword: (body: ForgotPasswordRequest) =>
    api.post<{ ok: true }>('/auth/forgot-password', body),
  resetPassword: (body: ResetPasswordRequest) =>
    api.post<AuthResponse>('/auth/reset-password', body),
};

export const classesApi = {
  list: () => api.get<{ classes: ZumbaClassWithBookingState[] }>('/classes'),
  get: (id: string) => api.get<{ class: ZumbaClassWithBookingState }>(`/classes/${id}`),
  book: (id: string) => api.post<{ ok: true }>(`/classes/${id}/book`),
  cancel: (id: string) => api.delete<{ ok: true }>(`/classes/${id}/book`),
};

export const meApi = {
  bookings: () => api.get<{ bookings: BookingWithClass[] }>('/me/bookings'),
  billing: () => api.get<MyBillingResponse>('/me/billing'),
  updateProfile: (body: UpdateProfileRequest) => api.patch<AuthResponse>('/me', body),
  updateNotifications: (body: UpdateNotificationPrefsRequest) =>
    api.patch<AuthResponse>('/me/notifications', body),
  changePassword: (body: ChangePasswordRequest) => api.post<{ ok: true }>('/me/password', body),
  async uploadAvatar(file: File): Promise<PublicUser> {
    const { uploadUrl, key } = await api.post<PresignUploadResponse>('/me/avatar/presign', {
      contentType: file.type as AllowedImageType,
    });
    await uploadToS3(uploadUrl, file);
    const { user } = await api.post<AuthResponse>('/me/avatar/confirm', { key });
    return user;
  },
};

export const settingsApi = {
  get: () => api.get<{ settings: SiteSettings }>('/settings'),
  update: (body: UpdateSettingsRequest) =>
    api.patch<{ settings: SiteSettings }>('/admin/settings', body),
};

export const adminApi = {
  customers: (params: { search?: string; page?: number; pageSize?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.search) qs.set('search', params.search);
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    const suffix = qs.toString() ? `?${qs}` : '';
    return api.get<PaginatedCustomers>(`/admin/customers${suffix}`);
  },
  customer: (id: string) => api.get<CustomerOverview>(`/admin/customers/${id}`),
  waiverPdfUrl: (id: string) => `/api/admin/customers/${id}/waiver/pdf`,
  adjustCredits: (id: string, amount: number, note?: string) =>
    api.post<BillingResult>(`/admin/customers/${id}/credits`, { amount, note }),
  purchasePackage: (id: string, size: number) =>
    api.post<BillingResult>(`/admin/customers/${id}/package`, { size }),
  recordDropIn: (id: string) => api.post<BillingResult>(`/admin/customers/${id}/dropin`),
  setSubscription: (id: string, active: boolean) =>
    api.post<BillingResult>(`/admin/customers/${id}/subscription`, { active }),
  addNote: (id: string, body: string) =>
    api.post<{ note: CrmNote }>(`/admin/customers/${id}/notes`, { body }),
  deleteNote: (customerId: string, noteId: string) =>
    api.delete<{ ok: true }>(`/admin/customers/${customerId}/notes/${noteId}`),
  signups: () => api.get<{ signups: PublicUser[] }>('/admin/signups'),
  createClass: (body: CreateClassRequest) =>
    api.post<{ class: ZumbaClass }>('/admin/classes', body),
  updateClass: (id: string, body: UpdateClassRequest) =>
    api.patch<{ class: ZumbaClass }>(`/admin/classes/${id}`, body),
  cancelClass: (id: string) => api.delete<{ ok: true }>(`/admin/classes/${id}`),
  roster: (id: string) =>
    api.get<{ class: ZumbaClass; roster: RosterEntry[] }>(`/admin/classes/${id}/roster`),
};
